import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, ChevronLeft, Home, BarChart2, Search, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SYMBOL = 'XAU/USD';
const FALLBACK_PRICE = 2680.40;
const N_CANDLES = 22;

const initialCandles = (basePrice) => {
  let last = basePrice;
  return Array.from({ length: N_CANDLES }).map((_, i) => {
    const range = basePrice * 0.004; // 0.4% volatility per candle
    const change = (Math.random() - 0.45) * range;
    const open = last;
    const close = last + change;
    const high = Math.max(open, close) + Math.random() * range * 0.4;
    const low = Math.min(open, close) - Math.random() * range * 0.4;
    last = close;
    return { i, open, high, low, close, up: close >= open };
  });
};

const LivePhoneMockup = ({ rotate = -4, className = '' }) => {
  const [basePrice, setBasePrice] = useState(FALLBACK_PRICE);
  const [candles, setCandles] = useState(() => initialCandles(FALLBACK_PRICE));
  const [price, setPrice] = useState(FALLBACK_PRICE);
  const [change24h, setChange24h] = useState(0);
  const [tickDir, setTickDir] = useState(null); // 'up' | 'down'

  // Real XAU/USD via the backend WebSocket — true tick-by-tick (~0.7s) feed
  useEffect(() => {
    const wsUrl = (API || '').replace(/^http/i, 'ws') + '/api/ws/prices';
    let ws;
    let stopped = false;
    let backoff = 1000;

    const handleQuote = (q) => {
      if (!q) return;
      const px = q.bid ?? q.price;
      if (!px) return;
      setChange24h(q.change_24h || 0);
      setPrice((prev) => {
        setTickDir(px > prev ? 'up' : px < prev ? 'down' : null);
        return px;
      });
      setBasePrice((prev) => (prev === FALLBACK_PRICE ? px : prev));
    };

    const connect = () => {
      try { ws = new WebSocket(wsUrl); } catch { return; }
      ws.onopen = () => { backoff = 1000; ws.send(JSON.stringify({ type: 'subscribe', symbols: [SYMBOL] })); };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if ((m.type === 'snapshot' || m.type === 'tick') && m.prices && m.prices[SYMBOL]) {
            handleQuote(m.prices[SYMBOL]);
          }
        } catch { /* noop */ }
      };
      ws.onclose = () => { if (!stopped) { setTimeout(connect, backoff); backoff = Math.min(backoff * 2, 15000); } };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => { stopped = true; try { ws?.close(); } catch {} };
  }, []);

  // Between API ticks, micro-jitter the price so the user sees motion every second
  useEffect(() => {
    const t = setInterval(() => {
      setPrice((p) => {
        const jitter = (Math.random() - 0.5) * (p * 0.0004); // ±0.04%
        const next = +(p + jitter).toFixed(2);
        setTickDir(next > p ? 'up' : next < p ? 'down' : null);
        return next;
      });
    }, 1100);
    return () => clearInterval(t);
  }, []);

  // Add a new candle every 1.8s using the latest live price
  useEffect(() => {
    const t = setInterval(() => {
      setCandles((cs) => {
        const last = cs[cs.length - 1];
        const open = last.close;
        const range = open * 0.003;
        const close = +((price ?? open) + (Math.random() - 0.5) * range).toFixed(2);
        const high = Math.max(open, close) + Math.random() * range * 0.5;
        const low = Math.min(open, close) - Math.random() * range * 0.5;
        const next = { i: last.i + 1, open, high, low, close, up: close >= open };
        return [...cs.slice(1), next];
      });
    }, 1800);
    return () => clearInterval(t);
  }, [price]);

  // Initialize the candles around the real price once it arrives
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && basePrice !== FALLBACK_PRICE) {
      setCandles(initialCandles(basePrice));
      setSeeded(true);
    }
  }, [basePrice, seeded]);

  // Chart bounds
  const highs = candles.map(c => c.high);
  const lows = candles.map(c => c.low);
  const maxP = Math.max(...highs);
  const minP = Math.min(...lows);
  const range = maxP - minP || 1;
  const chartH = 220;
  const chartW = 280;
  const candleW = chartW / N_CANDLES - 2;
  const y = (p) => ((maxP - p) / range) * (chartH - 20) + 10;

  const isUp = change24h >= 0;

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ transform: `rotate(${rotate}deg)`, filter: 'drop-shadow(0 30px 60px rgba(20,15,10,0.22))' }}
      data-testid="live-phone-mockup"
    >
      {/* iPhone frame */}
      <div className="relative w-[280px] h-[580px] bg-[#0A0D11] rounded-[44px] border-[3px] border-[#1A1F26] overflow-hidden">
        {/* Side rails */}
        <div className="absolute left-[-3px] top-[110px] w-[3px] h-[60px] bg-[#1A1F26] rounded-l-full" />
        <div className="absolute left-[-3px] top-[185px] w-[3px] h-[40px] bg-[#1A1F26] rounded-l-full" />
        <div className="absolute left-[-3px] top-[235px] w-[3px] h-[40px] bg-[#1A1F26] rounded-l-full" />
        <div className="absolute right-[-3px] top-[150px] w-[3px] h-[80px] bg-[#1A1F26] rounded-r-full" />

        {/* Screen */}
        <div className="absolute inset-[10px] rounded-[36px] bg-[#0B0F14] overflow-hidden flex flex-col">
          {/* Dynamic Island */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[88px] h-[24px] bg-black rounded-full z-30" />

          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 text-white text-[10px] font-semibold relative z-20">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span className="opacity-90">●●●●</span>
              <svg width="11" height="9" viewBox="0 0 12 10" fill="currentColor"><path d="M0 6L2 4L4 6L7 3L10 6L12 4V10H0V6Z" /></svg>
              <svg width="16" height="8" viewBox="0 0 17 8" fill="currentColor"><rect x="1" y="1" width="13" height="6" rx="1.5" stroke="white" strokeOpacity="0.6" fill="white" /></svg>
            </div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 mt-3 relative z-10">
            <button className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center text-white/90">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {/* VAROTRADES logo + title */}
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-md bg-gradient-to-br from-[#D85308] to-[#BC3F05] flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4L12 20L21 4" /></svg>
              </div>
              <span className="text-white text-[12.5px] font-bold tracking-wide">VAROTRADES</span>
            </div>
            <button className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center text-white/90 relative">
              <Bell className="w-4 h-4" />
              <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#D85308]" />
            </button>
          </div>

          {/* Symbol + price */}
          <div className="px-5 mt-5 relative z-10">
            <div className="text-white/55 text-[12px] font-medium">{SYMBOL}</div>
            <motion.div
              key={price}
              initial={{ scale: tickDir ? 1.04 : 1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.35 }}
              className="text-white text-[28px] font-bold tracking-tight tabular-nums leading-tight mt-0.5"
            >
              ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </motion.div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`text-[11px] ${isUp ? 'text-emerald-400' : 'text-red-400'} flex items-center gap-1`}>
                {isUp ? '▲' : '▼'}
                <span className="tabular-nums">{Math.abs(change24h).toFixed(2)}%</span>
                <span className="opacity-80">24h</span>
              </span>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 px-3 mt-4 relative">
            <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-full" preserveAspectRatio="none">
              {/* grid */}
              {[0.2, 0.4, 0.6, 0.8].map((p, i) => (
                <line key={i} x1="0" x2={chartW} y1={p * chartH} y2={p * chartH} stroke="white" strokeOpacity="0.05" />
              ))}
              <AnimatePresence initial={false}>
                {candles.map((c, idx) => {
                  const x = idx * (candleW + 2) + 1;
                  const yOpen = y(c.open);
                  const yClose = y(c.close);
                  const yHigh = y(c.high);
                  const yLow = y(c.low);
                  const bodyY = Math.min(yOpen, yClose);
                  const bodyH = Math.max(Math.abs(yClose - yOpen), 1.5);
                  const color = c.up ? '#D85308' : '#3A2218';
                  const lastIdx = candles.length - 1;
                  return (
                    <motion.g
                      key={c.i}
                      initial={{ opacity: 0, x: 6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -6 }}
                      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <line x1={x + candleW / 2} x2={x + candleW / 2} y1={yHigh} y2={yLow} stroke={color} strokeWidth="1" />
                      <rect x={x} y={bodyY} width={candleW} height={bodyH} fill={color} rx="0.5">
                        {idx === lastIdx && (
                          <animate attributeName="opacity" values="0.6;1;0.6" dur="1.4s" repeatCount="indefinite" />
                        )}
                      </rect>
                    </motion.g>
                  );
                })}
              </AnimatePresence>
              {/* current price line */}
              <line x1="0" x2={chartW} y1={y(price)} y2={y(price)} stroke="#D85308" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.5" />
            </svg>
          </div>

          {/* Timeframe tabs */}
          <div className="flex items-center justify-center gap-1.5 mt-1 px-4">
            {['1D', '1W', '1M', '3M', '1Y', 'All'].map((tf) => (
              <span
                key={tf}
                className={`text-[10.5px] font-semibold px-2 py-1 rounded-md ${tf === '1W' ? 'bg-[#D85308]/15 text-[#D85308]' : 'text-white/50'}`}
              >
                {tf}
              </span>
            ))}
          </div>

          {/* Buy / Sell */}
          <div className="flex gap-2 px-4 mt-3">
            <motion.button
              animate={{ boxShadow: ['0 0 0 0 rgba(216,83,8,0.6)', '0 0 0 8px rgba(216,83,8,0)'] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              className="flex-1 h-11 rounded-xl bg-[#D85308] text-white font-bold text-[14px] tracking-wide"
            >
              BUY
            </motion.button>
            <button className="flex-1 h-11 rounded-xl bg-[#1A1F26] text-white font-bold text-[14px] tracking-wide">
              SELL
            </button>
          </div>

          {/* Bottom nav */}
          <div className="flex items-center justify-around px-6 py-3 border-t border-white/5 mt-3">
            <Home className="w-4 h-4 text-white" />
            <BarChart2 className="w-4 h-4 text-white/35" />
            <Search className="w-4 h-4 text-white/35" />
            <User className="w-4 h-4 text-white/35" />
          </div>
          {/* Home indicator */}
          <div className="flex justify-center pb-2">
            <span className="w-24 h-1 rounded-full bg-white/30" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePhoneMockup;
